import React from "react"
import styled from "styled-components"
import PageSizeIcon from "./PageSizeIcon.svg"
import { TimePreset } from "../../../../../../Managers/VisualizationManager/Viewport/Components/XAxis"
import { SetterOrUpdater, useRecoilValue } from "recoil"
import { selectedLayoutGroupAtom } from "../../Atoms/Layout"
import { useDataReviewPermissions } from "../../../../../../Hooks/useDataReviewPermissions"
import { permissionTooltip } from "../../../../../../Utils/permissionUtils"
import { TimeBasedVisualizationConfig } from "../../Types/TimeBasedVisualizationConfig"
import { MdEast, MdModeEdit } from "react-icons/md"
import { BiLink, BiUnlink } from "react-icons/bi"
import { MobergButton, MobergButtonShape } from "../../../../../../Components/MobergButton/MobergButton"
import { MobergIconSize } from "../../../../../../Components/MobergIcon/MobergIcon"
import { MobergDropdown, MobergDropdownSize, MobergDropdownValue } from "../../../../../../Components/MobergDropdown/MobergDropdown"
import { MobergBoxShadow } from "../../../../../../Components/MobergThemes/MobergStyles"
import { MobergTheme } from "../../../../../../Components/MobergThemes/MobergColors"
import { MobergFontSize } from "../../../../../../Components/MobergFont/MobergFont"
import { useModalProvider } from "../../../../../../Providers/ModalProvider"
import { AiOutlineFieldTime } from "react-icons/ai"
import { MobergColumn, MobergRow } from "../../../../../../Moberg"
import { MobergText } from "../../../../../../Components/MobergHeader/MobergHeader"
import { currentPatientFileInfoAtom } from "../../Atoms/PatientFile"
import { getClampedStartAndEnd } from "../Visualizations/clamping"
import { SelectTimeRangeModal } from "./SelectTimeRangeModal"
import { ModalityJumpTarget } from "../../Utils/modalityJumpTargets"
import { linkedWindowsDispatch } from "../../Atoms/Visualizations"
import { LinkedWindowEventType } from "../../Types/LinkedWindowInfo"
import { useEnvironmentVariablesProvider } from "../../../../../../Providers/EnvironmentVariablesProvider"
import { WindowLoadingStateMessage } from "../Visualizations/TimeSeriesGraphGroup/React/WindowLoadingStateMessage"
import { useEventReview } from "../../../../../../Providers/EventReviewProvider"


function formatViewDuration(milliseconds: number): string {
	if (isNaN(milliseconds)) {
		milliseconds = 0
	}

	const seconds = Math.floor(milliseconds / 1000)
	const minutes = Math.floor(seconds / 60)
	const hours = Math.floor(minutes / 60)
	const days = Math.floor(hours / 24)

	let formattedDays = ""
	if (days === 0) {
		formattedDays = ""
	} else if (days === 1) {
		formattedDays = `${days} Day `
	} else {
		formattedDays = `${days} Days `
	}
	const formattedHours = hours % 24
	const formattedMinutes = minutes % 60
	const formattedSeconds = seconds % 60

	return `${formattedDays}${String(formattedHours).padStart(2, "0")}:${String(formattedMinutes).padStart(2, "0")}:${String(formattedSeconds).padStart(2, "0")}`
}

type VisualizationToolbarProps = {
	layoutId: string
	windowId: string
	setAtom: SetterOrUpdater<any>
	renderConfigureModal: () => void
	atomKey: string
	atomValue: TimeBasedVisualizationConfig
	viewDuration: number
	timePresetOptions: TimePreset[]
	visualizationSpecificSettings?: JSX.Element | JSX.Element[]
	actionButtons?: JSX.Element | JSX.Element[]
	viewDurationAnnotation?: string
	modalityTargets?: ModalityJumpTarget[]
}

export const VisualizationToolbar = ({
	layoutId,
	windowId,
	viewDuration,
	visualizationSpecificSettings,
	actionButtons,
	timePresetOptions,
	atomValue,
	setAtom,
	atomKey,
	renderConfigureModal,
	viewDurationAnnotation,
	modalityTargets,
}: VisualizationToolbarProps) => {
	const currentWindowTimePreset = timePresetOptions.find(preset => preset.time === viewDuration) || timePresetOptions[0]
	const { isAdmitted } = useRecoilValue(currentPatientFileInfoAtom)
	const { createModal, close } = useModalProvider()
	const { environmentVariables } = useEnvironmentVariablesProvider()
	const selectedLayoutGroup = useRecoilValue(selectedLayoutGroupAtom)
	const { canEditGroup } = useDataReviewPermissions()
	const canEditWindowSettings = canEditGroup(selectedLayoutGroup?.type)
	const eventReview = useEventReview()

	const [fileStartDate, fileEndDate] = atomValue.fileScale.domain()
	const linkTooltip = eventReview.isActive
		? "Center-sync linked windows"
		: "Sync linked windows"

	const updateViewDuration = (value: MobergDropdownValue) => {
		const preset = timePresetOptions.find(preset => preset.time === value && preset.label !== "Page Size")

		if (!preset) {
			return
		}

		setAtom((previous: TimeBasedVisualizationConfig) => {
			const viewStart = previous.viewScale.domain()[0]
			const newEndTime = viewStart.getTime() + preset.time
			const newStartTime = newEndTime - preset.time

			const { start, end } = getClampedStartAndEnd(newStartTime, newEndTime, previous.fileScale)

			return {
				...previous,
				viewScale: previous.viewScale.domain([start, end]),
				targetMmPerSecond: preset.targetMmPerSecond,
			}
		})
	}

	// Recoil callback allows us to read the live end date without subscribing to the value in this component.
	const handleLiveModeToggle = () => {
		// Update live mode enabled and disable linking on the current window only.
		setAtom((previous: TimeBasedVisualizationConfig) => (
			{ 
				...previous, 
				liveModeEnabled: !previous.liveModeEnabled,
				isLinked: false
			}
		))
	}

	const goToTime = (start: number, end: number) => {
		linkedWindowsDispatch.call("update", undefined, {
			type: LinkedWindowEventType.JUMP_END,
			date: new Date(end),
			controller: windowId,
			options: { autoScale: true, controllerIsLinked: atomValue.isLinked, jumpSelf: true },
		})
	}

	function renderTimeSelector() {
		const [currentTime, ] = atomValue.viewScale.domain()

		createModal(<SelectTimeRangeModal
			fileStart={fileStartDate}
			fileEnd={fileEndDate}
			timeZone={atomValue.timeZone}
			viewDurationLabel={currentWindowTimePreset.label}
			viewDurationMs={viewDuration}
			initialStart={currentTime}
			onSave={goToTime}
			onClose={close}
			modalityTargets={modalityTargets}
			liveModeEnabled={atomValue.liveModeEnabled}
		/>)
	}

	return (
		<WindowSettingsBar>
			<div style={{ flex: 1, display: "flex", gap: "16px", justifyContent: "space-between" }}>
				<MobergRow gap="16px" expand={false}>
					<MobergRow gap="8px" expand={false}>
						<MobergColumn horizontalAlign="center" style={{ lineHeight: "90%" }}>
							<MobergText style={{ fontWeight: "bold" }}>
								{formatViewDuration(viewDuration)}
							</MobergText>
							<div>
								<img src={PageSizeIcon} height={8} alt="PageSizeIcon" />
							</div>
							{viewDurationAnnotation && (
								<MobergText fontSize={MobergFontSize.SMALL} style={{ color: "var(--color-text-version)" }}>
									{viewDurationAnnotation}
								</MobergText>
							)}
						</MobergColumn>

						<MobergDropdown
							size={MobergDropdownSize.SMALL}
							selectedValue={currentWindowTimePreset.time}
							onChange={updateViewDuration}
							options={timePresetOptions.map(preset => ({ label: preset.label, value: preset.time })).filter(preset => preset.label !== "Page Size")}
						/>
					</MobergRow>

					<WindowLoadingStateMessage atomKey={atomKey} />
				</MobergRow>

				<MobergRow expand={false} horizontalAlign="right" gap="16px" style={{ flex: 1 }}>
					{visualizationSpecificSettings}
				</MobergRow>
			</div>

			<div style={{ flex: 1, display: "flex", gap: "4px", alignItems: "center", justifyContent: "left"}}>
				{actionButtons}

				{environmentVariables.FEATURE_FLAG_TIME_SELECTOR && (
					<MobergButton
						onClick={renderTimeSelector}
						shape={MobergButtonShape.SQUARE}
						disabled={atomValue.liveModeEnabled}
						tooltip="Set the time range manually"
					>
						<AiOutlineFieldTime size={MobergIconSize.SMALL}/>
					</MobergButton>
				)}

				<MobergButton
					shape={MobergButtonShape.SQUARE}
					theme={atomValue.isLinked ? MobergTheme.BLUE : MobergTheme.BLACK}
					tooltip={linkTooltip}
					active={atomValue.isLinked}
					disabled={atomValue.liveModeEnabled}
					onClick={() => setAtom((previous: TimeBasedVisualizationConfig) => ({ ...previous, isLinked: !previous.isLinked }))}
				>
					{atomValue.isLinked ? <BiLink size={MobergIconSize.SMALL} /> : <BiUnlink size={MobergIconSize.SMALL} />}
				</MobergButton>

				<MobergButton
					theme={atomValue.liveModeEnabled ? MobergTheme.BLUE : MobergTheme.BLACK}
					tooltip={isAdmitted 
						? "Lock the window to the latest data" 
						: "Lock the window to the latest data (disabled because the patient is not being recorded)"
					}
					active={atomValue.liveModeEnabled}
					disabled={!isAdmitted}
					shape={MobergButtonShape.SQUARE}
					fontSize={MobergFontSize.SMALL}
					onClick={handleLiveModeToggle}
					style={{ gap: "4px" }}
				>
					<MdEast size={MobergIconSize.SMALL} viewBox={"0 0 25 25"}/>
				</MobergButton>
				
			</div>

			<MobergRow style={{ justifyContent: "right" }} expand={false} gap="16px">
				<MobergButton
					onClick={renderConfigureModal}
					shape={MobergButtonShape.SQUARE}
					theme={MobergTheme.BLUE}
					tooltip={permissionTooltip(canEditWindowSettings) || "Edit window settings"}
					disabled={!canEditWindowSettings}
					dataTestId="window-edit-settings"
				>
					<MdModeEdit size={MobergIconSize.SMALL} />
				</MobergButton>
			</MobergRow>


		</WindowSettingsBar>
	)
}

// TODO: at some point, these need to be replaced with standard form components
const WindowSettingsBar = styled.div`
	display: flex;
	gap: 32px;
	align-items: center;
	height: 50px;
	white-space: nowrap;
	border-radius: 6px 6px 0 0;
	box-shadow: ${MobergBoxShadow.LOW};
	padding: 5px;
	justify-content: space-between;
	overflow-x: auto;
	color: var(--text-color);
	background: var(--color-surface-panel);
`

export const SettingContainer = styled.div`
	display: flex;
	align-items: center;

	p {
		font-family: "Source Sans Pro";
		font-style: normal;
		font-weight: 700;
		font-size: 14px;
		line-height: 150%;
		text-align: center;
		color: var(--text-color);
		margin: 0;
	}
`

export const SettingDropdown = styled.select`
	border-radius: 6px;
	display: flex;
	justify-content: center;
	text-align: center;
`
